import { SelectProps, Space, Spin } from 'antd';
import { FieldHelperProps, FieldInputProps, FieldMetaProps } from 'formik';
import React, { FC, ReactNode, useCallback } from 'react';
import { ErrorDiv, ErrorLabel, FormItem } from '../formInput/styles';
import { Option } from 'components/ui/base/select/Select';
import { GlobalSelectStyles, RefWrapper, StyledSelect, Wrapper } from './styles';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';

export interface SelectOption {
    id: string | number;
    value: string;
    disabled?: boolean;
}

export type FormSelectProps = Pick<
    SelectProps,
    | 'value'
    | 'dropdownRender'
    | 'filterOption'
    | 'dropdownMatchSelectWidth'
    | 'dropdownStyle'
    | 'optionLabelProp'
    | 'searchValue'
    | 'onDropdownVisibleChange'
> & {
    mode?: SelectProps['mode'];
    label?: string;
    selectOptions?: Readonly<SelectOption[]>;
    children?: ReactNode;
    fieldProps?: FieldInputProps<any>;
    fieldMeta?: FieldMetaProps<any>;
    fieldHelper?: FieldHelperProps<any>;
    required?: boolean;
    placeholder?: string;
    hideKeys?: boolean;
    disabled?: boolean;
    refNumber?: string;
    style?: React.CSSProperties;
    loading?: boolean;
    onChange?: (value: any) => void;
    onSearch?: (val: string) => void;
    onClear?: () => void;
    renderOptionContent?: (item: SelectOption, label: string | number) => ReactNode;
    getOptionLabel?: (item: SelectOption) => ReactNode;
};

export interface SelectSearchOption {
    children?: string;
    value?: string | number;
    label?: string;
    key?: string | number;
}

const FormSelect: FC<FormSelectProps> = ({
    label,
    selectOptions,
    fieldProps,
    fieldMeta,
    required,
    fieldHelper,
    placeholder,
    hideKeys,
    disabled = false,
    loading,
    mode,
    value,
    refNumber,
    onChange,
    onClear,
    onSearch,
    style,
    renderOptionContent,
    getOptionLabel,
    dropdownRender,
    dropdownMatchSelectWidth,
    dropdownStyle,
    filterOption,
    optionLabelProp,
    searchValue,
    onDropdownVisibleChange,
}) => {
    const handleEmptyValues = useCallback((item: SelectOption) => {
        return !!item.value ? item.value : item.id;
    }, []);

    const onMouseClicked = (e: any) => {
        e.stopPropagation();
        e.preventDefault();
    };

    const getOptionText = (item: SelectOption) => {
        return !hideKeys ? `${item.id} - ${item.value}` : handleEmptyValues(item);
    };

    const getOptionContent = (item: SelectOption): string | ReactNode => {
        const text = getOptionText(item);

        if (renderOptionContent) {
            return renderOptionContent(item, text);
        }

        return text;
    };

    const defaultFilterOption = (input: string, option?: SelectSearchOption) => {
        if (!option) return false;
        const inputLower = input.toLowerCase();
        const labelText = typeof option.label === 'string' ? option.label.toLowerCase() : '';
        const id =
            typeof option.value === 'string' ? option.value.toLowerCase() : String(option.value ?? '').toLowerCase();
        return labelText.includes(inputLower) || id.includes(inputLower);
    };

    return (
        <Wrapper>
            <GlobalSelectStyles />
            <Space size={8}>
                {refNumber && <RefWrapper>{refNumber}</RefWrapper>}
                {required && <LabelRequiredSymbol />}
                {label && <span>{label}</span>}
            </Space>
            <FormItem style={style} validateStatus={fieldMeta?.error && !!fieldMeta?.touched ? 'error' : ''}>
                <StyledSelect
                    id={fieldProps?.name ?? ''}
                    mode={mode}
                    disabled={disabled}
                    placeholder={placeholder ?? ''}
                    showSearch
                    allowClear
                    onSearch={onSearch}
                    searchValue={searchValue}
                    onDropdownVisibleChange={onDropdownVisibleChange}
                    onClear={onClear}
                    {...fieldProps}
                    value={(value ?? fieldProps?.value) || undefined}
                    status={fieldMeta?.error && !!fieldMeta?.touched ? 'error' : ''}
                    filterOption={filterOption ?? defaultFilterOption}
                    dropdownRender={dropdownRender ?? (loading ? (menu) => <Spin spinning>{menu}</Spin> : undefined)}
                    dropdownMatchSelectWidth={dropdownMatchSelectWidth}
                    dropdownStyle={dropdownStyle}
                    optionLabelProp={optionLabelProp}
                    onChange={(value: any) => {
                        if (value) {
                            onChange?.(value);
                            fieldProps?.onChange({ target: { value, name: fieldProps.name } });
                        } else {
                            fieldHelper?.setValue(null);
                        }
                    }}
                    onBlur={(value: any) => {
                        fieldHelper?.setTouched(true);
                    }}
                    onClick={onMouseClicked}
                >
                    {selectOptions?.map((item: SelectOption, i: number) => {
                        const text = getOptionText(item);
                        const content = getOptionContent(item);
                        const label = getOptionLabel ? getOptionLabel(item) : text;
                        return (
                            <Option
                                onClick={onMouseClicked}
                                key={item.id ?? i}
                                value={item.id}
                                label={label}
                                disabled={item.disabled}
                            >
                                {content}
                            </Option>
                        );
                    })}
                </StyledSelect>
                {fieldMeta?.error && fieldMeta?.touched && (
                    <ErrorDiv error={!!fieldMeta?.error} touched={!!fieldMeta?.touched}>
                        {!!fieldMeta?.error && !!fieldMeta?.touched && <ErrorLabel>{fieldMeta?.error}</ErrorLabel>}
                    </ErrorDiv>
                )}
            </FormItem>
        </Wrapper>
    );
};

export default FormSelect;
